import { z } from "zod";
import { currencySchema, unitAmountSchema } from "./catalogItemMoney.schema";

// --- Batch price operations (embedded in updateCatalogItemSchema's `prices`
// field, see catalogItem.schema.ts) ---
//
// This is the ONLY way a catalog item's prices are ever mutated. Nothing here
// is persisted independently: an update request's `prices` array is applied
// inside the same transaction as the item's own field changes, so a "Save" on
// the catalog item form commits the item edit and every staged price
// create/archive/enable/delete/set-default/description change together,
// atomically, or not at all. There is deliberately no single-price
// create/update/delete endpoint — an earlier version of this feature had one
// (createCatalogItemPrice/updateCatalogItemPrice/deleteCatalogItemPrice) and
// it was removed because it let a price persist before the item's Save,
// which the product explicitly does not want.
export const createCatalogItemPriceOperationSchema = z.object({
    op: z.literal("create"),
    // Client-generated (not the eventual server id) so the response can map
    // created prices back to the staged rows that requested them.
    clientId: z.string().min(1),
    unitAmount: unitAmountSchema,
    currency: currencySchema,
    isDefault: z.boolean().optional(),
    description: z.string().max(1000).nullable().optional(),
});

// Plain object (no .refine) so this stays a valid discriminatedUnion member
// below; "at least one field provided" is enforced by
// catalogItemPriceOperationSchema's top-level superRefine instead.
export const updateCatalogItemPriceOperationSchema = z.object({
    op: z.literal("update"),
    priceId: z.string().uuid(),
    active: z.boolean().optional(),
    isDefault: z.literal(true).optional(),
    description: z.string().max(1000).nullable().optional(),
});

export const deleteCatalogItemPriceOperationSchema = z.object({
    op: z.literal("delete"),
    priceId: z.string().uuid(),
});

// Exported (not inlined into a .superRefine callback) so agentTools' MCP
// price-operation schema - which reuses the update/delete variants verbatim
// but swaps in a currency-less create variant (see catalog-items/schemas.ts)
// - can apply the identical "at least one field" invariant instead of
// duplicating it.
export function assertPriceOperationHasUpdateField<
    T extends {
        op: string;
        active?: boolean;
        isDefault?: boolean;
        description?: string | null;
    },
>(value: T, ctx: z.RefinementCtx): void {
    if (
        value.op === "update" &&
        value.active === undefined &&
        value.isDefault === undefined &&
        value.description === undefined
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one price field must be provided",
        });
    }
}

export const catalogItemPriceOperationSchema = z
    .discriminatedUnion("op", [
        createCatalogItemPriceOperationSchema,
        updateCatalogItemPriceOperationSchema,
        deleteCatalogItemPriceOperationSchema,
    ])
    .superRefine(assertPriceOperationHasUpdateField);

// Shared cross-op invariant for both the full batch (update) and the
// create-only batch (create): `clientId` is the caller's key for mapping
// created prices back to staged rows in the response, so duplicates within
// one batch would let two persisted prices collide on the same key — reject
// them here rather than silently returning an ambiguous mapping. Exported so
// agentTools' MCP-specific (currency-less) price batch schemas can reuse the
// same invariant instead of duplicating it (see catalog-items/schemas.ts).
export function assertUniqueCreateClientIds<
    T extends { op: string; clientId?: string },
>(operations: T[], ctx: z.RefinementCtx): void {
    const seenClientIds = new Set<string>();
    operations.forEach((operation, index) => {
        if (operation.op !== "create" || operation.clientId === undefined) {
            return;
        }
        if (seenClientIds.has(operation.clientId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "clientId must be unique within a price batch",
                path: [index, "clientId"],
            });
            return;
        }
        seenClientIds.add(operation.clientId);
    });
}

// The batch array itself, with the cross-op invariants that a single
// operation can't see (see assertUniqueCreateClientIds above).
export const catalogItemPriceOperationsSchema = z
    .array(catalogItemPriceOperationSchema)
    .max(100)
    .superRefine(assertUniqueCreateClientIds);

// Create-only batch: embedded in createCatalogItemSchema's `prices` field so
// a catalog item can be created with several starting prices (e.g. multiple
// currencies) in one request. There's nothing to update/delete on an item
// that doesn't exist yet, so this is deliberately narrower than
// catalogItemPriceOperationsSchema — only "create" operations are valid here.
// Applied via the same CatalogItemMutationInternalService.applyPriceOperations
// used by updateCatalogItem's batch, inside the item's create transaction.
export const createCatalogItemPriceOperationsSchema = z
    .array(createCatalogItemPriceOperationSchema)
    .max(100)
    .superRefine(assertUniqueCreateClientIds);

export type CreateCatalogItemPriceOperation = z.infer<
    typeof createCatalogItemPriceOperationSchema
>;
export type UpdateCatalogItemPriceOperation = z.infer<
    typeof updateCatalogItemPriceOperationSchema
>;
export type DeleteCatalogItemPriceOperation = z.infer<
    typeof deleteCatalogItemPriceOperationSchema
>;
export type CatalogItemPriceOperation = z.infer<
    typeof catalogItemPriceOperationSchema
>;
